<script setup>
// Sarrdeh: "AI Train" - edit the system prompts of the n8n AI agents.
// Text is stored by the n8n workflow "ai train (chatwoot)" in the data table ai_prompts;
// every AI reads it right before replying, so a save applies from the next message.
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const API = 'https://automation.sarrdeh-tech.com/webhook/ai-train';

const SECTIONS = [
  {
    key: 'company',
    group: 'Shared',
    label: 'Company info',
    icon: 'i-lucide-building-2',
    usedBy: 'All 5 AIs',
    help: 'Services, portfolio and contact details. Every AI (messages and comments) knows this.',
  },
  {
    key: 'dm_rules',
    group: 'Shared',
    label: 'Message rules',
    icon: 'i-lucide-message-square-text',
    usedBy: 'Facebook, Instagram and WhatsApp messages',
    help: 'How the AI talks in private messages: language, Syrian dialect, length, style, attachments.',
  },
  {
    key: 'faq',
    group: 'Shared',
    label: 'FAQ (approved answers)',
    icon: 'i-lucide-circle-help',
    usedBy: 'Facebook, Instagram and WhatsApp messages (not public comments)',
    help: 'Questions and the exact answers the AI may give: prices, timelines, packages, offers. The AI quotes only what is written here.',
  },
  {
    key: 'media_reply',
    group: 'Shared',
    label: 'Photo & file reply',
    icon: 'i-lucide-image-off',
    usedBy: 'Facebook, Instagram and WhatsApp messages',
    help: 'Not the AI: this exact line is sent when someone sends only a photo, video, file or sticker (at most once every 10 minutes per chat). Photos never reach the AI.',
  },
  {
    key: 'facebook_dm',
    group: 'Facebook',
    label: 'Facebook Messages',
    icon: 'i-woot-facebook',
    usedBy: 'Facebook Messages AI',
    help: 'Extra instructions for Messenger only. Added after "Message rules".',
  },
  {
    key: 'facebook_comment',
    group: 'Facebook',
    label: 'Facebook Comments',
    icon: 'i-woot-facebook',
    usedBy: 'Facebook Comments AI',
    help: 'All rules for public replies under Facebook comments.',
  },
  {
    key: 'instagram_dm',
    group: 'Instagram',
    label: 'Instagram Messages',
    icon: 'i-woot-instagram',
    usedBy: 'Instagram Messages AI',
    help: 'Extra instructions for Instagram DMs only. Added after "Message rules".',
  },
  {
    key: 'instagram_comment',
    group: 'Instagram',
    label: 'Instagram Comments',
    icon: 'i-woot-instagram',
    usedBy: 'Instagram Comments AI',
    help: 'All rules for public replies under Instagram comments.',
  },
  {
    key: 'whatsapp_dm',
    group: 'WhatsApp',
    label: 'WhatsApp',
    icon: 'i-woot-whatsapp',
    usedBy: 'WhatsApp Business AI',
    help: 'Extra instructions for WhatsApp only. Added after "Message rules".',
  },
];
const GROUPS = ['Shared', 'Facebook', 'Instagram', 'WhatsApp'];

// AI Control: one on/off switch per AI. Off = the AI stops replying; messages still arrive in
// Chatwoot and replies typed here are still delivered.
const SWITCH_GROUPS = [
  {
    label: 'Facebook',
    icon: 'i-woot-facebook',
    items: [
      { key: 'facebook_dm', label: 'Messages' },
      { key: 'facebook_comment', label: 'Comments' },
    ],
  },
  {
    label: 'Instagram',
    icon: 'i-woot-instagram',
    items: [
      { key: 'instagram_dm', label: 'Messages' },
      { key: 'instagram_comment', label: 'Comments' },
    ],
  },
  {
    label: 'WhatsApp',
    icon: 'i-woot-whatsapp',
    items: [{ key: 'whatsapp_dm', label: 'Messages' }],
  },
];
// "Try it": chat with one AI using the current text, including unsaved edits. Nothing is saved or sent.
const TRY_AGENTS = [
  { key: 'facebook_dm', label: 'Facebook Messages' },
  { key: 'facebook_comment', label: 'Facebook Comments' },
  { key: 'instagram_dm', label: 'Instagram Messages' },
  { key: 'instagram_comment', label: 'Instagram Comments' },
  { key: 'whatsapp_dm', label: 'WhatsApp' },
];
const showTry = ref(false);
const tryAgent = ref('facebook_dm');
const tryMessages = ref([]);
const tryInput = ref('');
const trySending = ref(false);

const switches = ref({});
const switchBusy = ref('');
// Seconds a message AI waits for more messages before replying (so bursts get one answer).
const waits = ref({});
const waitDrafts = ref({});
const waitLimits = ref({ min: 5, max: 300, default: 50 });
const waitBusy = ref('');
const isOn = key => switches.value[key]?.state !== 'off';
const switchTitle = key => {
  const s = switches.value[key];
  if (!s || !s.updated_by) return '';
  const when = s.updatedAt ? new Date(s.updatedAt).toLocaleString() : '';
  return `Turned ${s.state} by ${s.updated_by}${when ? `, ${when}` : ''}`;
};

const currentUser = useMapGetter('getCurrentUser');

const parts = ref({});
const drafts = ref({});
const selectedKey = ref('company');
const isLoading = ref(true);
const isSaving = ref(false);
const loadError = ref('');
const showHistory = ref(false);

const selected = computed(() => SECTIONS.find(s => s.key === selectedKey.value));
const part = computed(() => parts.value[selectedKey.value] || null);
const savedText = key => {
  const p = parts.value[key];
  if (!p) return '';
  return p.live ? p.live.prompt : p.default;
};
const isDirty = key =>
  parts.value[key] !== undefined && drafts.value[key] !== savedText(key);
const anyDirty = computed(() => SECTIONS.some(s => isDirty(s.key)));
const draft = computed({
  get: () => drafts.value[selectedKey.value] ?? '',
  set: value => {
    drafts.value = { ...drafts.value, [selectedKey.value]: value };
  },
});
const isDefaultText = computed(
  () => part.value && draft.value === part.value.default
);

const formatDate = value => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
};

const call = async body => {
  const token = currentUser.value?.access_token;
  if (!token) {
    throw new Error(
      'Your user has no API access token, so AI Train cannot sign you in.'
    );
  }
  const response = await fetch(API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, token }),
  });
  let data = {};
  try {
    data = await response.json();
  } catch (e) {
    data = {};
  }
  if (!response.ok || !data.ok) {
    throw new Error(data.error || `Request failed (${response.status}).`);
  }
  return data;
};

const load = async ({ keepDrafts = false } = {}) => {
  isLoading.value = !keepDrafts;
  loadError.value = '';
  try {
    const data = await call({ action: 'load' });
    const nextParts = {};
    const nextDrafts = { ...drafts.value };
    data.parts.forEach(p => {
      nextParts[p.key] = p;
      if (!keepDrafts || nextDrafts[p.key] === undefined) {
        nextDrafts[p.key] = p.live ? p.live.prompt : p.default;
      }
    });
    parts.value = nextParts;
    drafts.value = nextDrafts;
    switches.value = data.switches || {};
    waits.value = data.waits || {};
    waitLimits.value = data.waitLimits || waitLimits.value;
    waitDrafts.value = Object.fromEntries(
      Object.entries(waits.value).map(([k, v]) => [k, String(v.seconds)])
    );
  } catch (e) {
    loadError.value = e.message;
  } finally {
    isLoading.value = false;
  }
};

const save = async () => {
  const key = selectedKey.value;
  if (!draft.value.trim()) {
    useAlert('The text cannot be empty. Use "Reset to built-in" instead.');
    return;
  }
  isSaving.value = true;
  try {
    await call({ action: 'save', key, prompt: draft.value });
    // The server trims the text; keep the box in sync with what was stored.
    drafts.value = { ...drafts.value, [key]: draft.value.trim() };
    await load({ keepDrafts: true });
    useAlert(`${selected.value.label} saved. The AI uses it from the next message.`);
  } catch (e) {
    useAlert(e.message);
  } finally {
    isSaving.value = false;
  }
};

const toggleSwitch = async (group, item) => {
  const state = isOn(item.key) ? 'off' : 'on';
  const name = `${group.label} ${item.label}`;
  if (
    state === 'off' &&
    // eslint-disable-next-line no-alert
    !window.confirm(
      `Turn off the ${name} AI?\n\nMessages will still arrive in your inbox and your replies will still be sent, but the AI will not answer until you turn it back on.`
    )
  ) {
    return;
  }
  switchBusy.value = item.key;
  try {
    await call({ action: 'switch', key: item.key, state });
    switches.value = {
      ...switches.value,
      [item.key]: {
        state,
        updated_by: currentUser.value?.name || '',
        updatedAt: new Date().toISOString(),
      },
    };
    useAlert(
      state === 'off'
        ? `${name} AI is off. It won't reply until you turn it back on.`
        : `${name} AI is on. It replies from the next message.`
    );
  } catch (e) {
    useAlert(e.message);
  } finally {
    switchBusy.value = '';
  }
};

const saveWait = async (group, item) => {
  const current = waits.value[item.key]?.seconds;
  const raw = String(waitDrafts.value[item.key] ?? '').trim();
  const seconds = Number(raw);
  if (raw === String(current)) return;
  const { min, max } = waitLimits.value;
  if (!Number.isInteger(seconds) || seconds < min || seconds > max) {
    useAlert(`Wait must be a whole number between ${min} and ${max} seconds.`);
    waitDrafts.value = { ...waitDrafts.value, [item.key]: String(current) };
    return;
  }
  waitBusy.value = item.key;
  try {
    await call({ action: 'wait', key: item.key, seconds });
    waits.value = {
      ...waits.value,
      [item.key]: {
        seconds,
        updated_by: currentUser.value?.name || '',
        updatedAt: new Date().toISOString(),
      },
    };
    useAlert(`${group.label} messages: the AI now waits ${seconds} s for more messages before replying.`);
  } catch (e) {
    useAlert(e.message);
    waitDrafts.value = { ...waitDrafts.value, [item.key]: String(current) };
  } finally {
    waitBusy.value = '';
  }
};
const waitTitle = key => {
  const w = waits.value[key];
  const base = `Seconds the AI waits for more messages before replying (${waitLimits.value.min}-${waitLimits.value.max}). Press Enter to save.`;
  if (!w || !w.updated_by) return base;
  const when = w.updatedAt ? new Date(w.updatedAt).toLocaleString() : '';
  return `${base}\nSet to ${w.seconds} s by ${w.updated_by}${when ? `, ${when}` : ''}`;
};

const unsavedCount = computed(() => SECTIONS.filter(s => isDirty(s.key)).length);
const sendTry = async () => {
  const text = tryInput.value.trim();
  if (!text || trySending.value) return;
  tryMessages.value = [...tryMessages.value, { role: 'user', content: text }];
  tryInput.value = '';
  trySending.value = true;
  try {
    const overrides = Object.fromEntries(
      SECTIONS.filter(s => isDirty(s.key)).map(s => [s.key, drafts.value[s.key]])
    );
    const data = await call({
      action: 'test',
      agent: tryAgent.value,
      overrides,
      messages: tryMessages.value.map(m => ({ role: m.role, content: m.content })),
    });
    tryMessages.value = [
      ...tryMessages.value,
      { role: 'assistant', content: data.reply, handoff: data.handoff, handoffSummary: data.handoffSummary },
    ];
  } catch (e) {
    tryMessages.value = [...tryMessages.value, { role: 'error', content: e.message }];
  } finally {
    trySending.value = false;
  }
};
const clearTry = () => {
  tryMessages.value = [];
};
const changeTryAgent = key => {
  tryAgent.value = key;
  tryMessages.value = [];
};

const discard = () => {
  draft.value = savedText(selectedKey.value);
};
const resetToDefault = () => {
  draft.value = part.value.default;
};
const restore = version => {
  draft.value = version.prompt;
  showHistory.value = false;
  useAlert('Version loaded into the editor. Press Save to make it live.');
};
const select = key => {
  selectedKey.value = key;
  showHistory.value = false;
};

const warnOnUnload = event => {
  if (!anyDirty.value) return;
  event.preventDefault();
  // eslint-disable-next-line no-param-reassign
  event.returnValue = '';
};
onMounted(() => {
  load();
  window.addEventListener('beforeunload', warnOnUnload);
});
onBeforeUnmount(() => window.removeEventListener('beforeunload', warnOnUnload));
onBeforeRouteLeave(() => {
  if (!anyDirty.value) return true;
  // eslint-disable-next-line no-alert
  return window.confirm('You have unsaved AI Train changes. Leave without saving?');
});
</script>

<template>
  <div class="flex flex-col w-full h-full pb-20 overflow-y-auto md:pb-0 md:overflow-hidden bg-n-background">
    <header class="flex flex-wrap items-center justify-between gap-3 px-4 py-4 md:px-6 border-b border-n-weak">
      <div class="min-w-0">
        <h1 class="text-lg font-medium text-n-slate-12">AI Train</h1>
        <p class="text-sm text-n-slate-11">
          Edit what the AI knows and how it replies. Saved changes apply from the next message.
        </p>
      </div>
      <div class="flex items-center gap-2 shrink-0">
        <Button
          :label="showTry ? 'Close test chat' : 'Try it'"
          icon="i-lucide-message-circle"
          :variant="showTry ? 'faded' : 'solid'"
          size="sm"
          :disabled="isLoading || !!loadError"
          @click="showTry = !showTry"
        />
        <Button
          label="Reload"
          icon="i-lucide-refresh-cw"
          variant="ghost"
          color="slate"
          size="sm"
          :is-loading="isLoading"
          @click="load()"
        />
      </div>
    </header>

    <div v-if="isLoading" class="flex items-center justify-center flex-1 text-sm text-n-slate-11">
      Loading prompts...
    </div>

    <div v-else-if="loadError" class="flex flex-col items-center justify-center flex-1 gap-3 px-6 text-center">
      <span class="i-lucide-triangle-alert size-6 text-n-ruby-9" />
      <p class="text-sm text-n-slate-12">{{ loadError }}</p>
      <Button label="Try again" size="sm" @click="load()" />
    </div>

    <section v-if="!isLoading && !loadError" class="px-4 py-3 md:px-6 border-b border-n-weak">
      <div class="flex flex-wrap items-center gap-2 mb-2">
        <span class="i-lucide-power size-4 text-n-slate-11" />
        <h2 class="text-sm font-medium text-n-slate-12">AI Control</h2>
        <span class="text-xs text-n-slate-10">
          Off = the AI stops replying. Messages still arrive here and your replies are still sent.
        </span>
      </div>
      <div class="flex flex-wrap gap-3">
        <div
          v-for="group in SWITCH_GROUPS"
          :key="group.label"
          class="flex flex-wrap items-center w-full sm:w-auto gap-x-4 gap-y-2 px-3 py-2 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <span class="flex items-center w-full sm:w-auto gap-1.5 text-sm font-medium text-n-slate-12">
            <span :class="group.icon" class="size-4" />
            {{ group.label }}
          </span>
          <button
            v-for="item in group.items"
            :key="item.key"
            type="button"
            role="switch"
            :aria-checked="isOn(item.key)"
            :title="switchTitle(item.key)"
            :disabled="switchBusy === item.key"
            class="flex items-center gap-2 text-sm text-n-slate-11 disabled:opacity-50"
            @click="toggleSwitch(group, item)"
          >
            {{ item.label }}
            <span
              class="relative h-4 rounded-full w-7 shrink-0 transition-colors"
              :class="isOn(item.key) ? 'bg-n-teal-9' : 'bg-n-slate-6'"
            >
              <span
                class="absolute top-0.5 size-3 rounded-full bg-white shadow transition-all"
                :class="isOn(item.key) ? 'left-[14px]' : 'left-0.5'"
              />
            </span>
            <span
              class="w-6 text-xs font-medium"
              :class="isOn(item.key) ? 'text-n-teal-11' : 'text-n-ruby-11'"
            >{{ isOn(item.key) ? 'ON' : 'OFF' }}</span>
          </button>
          <label
            v-for="item in group.items.filter(i => waits[i.key])"
            :key="`wait-${item.key}`"
            class="flex items-center gap-1.5 text-sm text-n-slate-11"
            :title="waitTitle(item.key)"
          >
            <span class="i-lucide-timer size-3.5" />
            Wait
            <input
              v-model="waitDrafts[item.key]"
              type="number"
              inputmode="numeric"
              :min="waitLimits.min"
              :max="waitLimits.max"
              step="1"
              :disabled="waitBusy === item.key"
              class="w-14 px-1.5 py-0.5 text-sm text-center border rounded-md border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-n-brand disabled:opacity-50"
              @keydown.enter.prevent="$event.target.blur()"
              @blur="saveWait(group, item)"
            />
            s
          </label>
        </div>
      </div>
    </section>

    <div v-if="!isLoading && !loadError" class="flex flex-col md:flex-row md:flex-1 md:min-h-0">
      <nav class="flex gap-4 p-3 overflow-x-auto border-b md:flex-col md:overflow-x-visible md:overflow-y-auto md:border-b-0 md:border-r md:w-60 shrink-0 border-n-weak">
        <div v-for="group in GROUPS" :key="group" class="flex flex-col gap-0.5 shrink-0 md:shrink">
          <p class="px-2 pb-1 text-xs font-medium uppercase whitespace-nowrap text-n-slate-10">{{ group }}</p>
          <button
            v-for="section in SECTIONS.filter(s => s.group === group)"
            :key="section.key"
            type="button"
            class="flex items-center gap-2 px-2 py-1.5 text-sm text-left rounded-lg whitespace-nowrap"
            :class="
              section.key === selectedKey
                ? 'bg-n-alpha-2 text-n-slate-12 font-medium'
                : 'text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="select(section.key)"
          >
            <span :class="section.icon" class="size-4 shrink-0" />
            <span class="flex-1 truncate">{{ section.label }}</span>
            <span
              v-if="switches[section.key] && !isOn(section.key)"
              class="text-[10px] font-medium text-n-ruby-11"
              title="This AI is switched off (AI Control)"
            >AI off</span>
            <span
              v-if="isDirty(section.key)"
              class="rounded-full size-2 bg-n-amber-9"
              title="Unsaved changes"
            />
            <span
              v-else-if="parts[section.key]?.live"
              class="text-[10px] text-n-teal-11"
              title="Edited (not the built-in text)"
            >edited</span>
          </button>
        </div>
      </nav>

      <section v-if="selected && part" class="flex flex-col flex-1 min-w-0 gap-3 p-4 md:p-6 md:overflow-y-auto">
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="min-w-0">
            <h2 class="flex items-center gap-2 text-base font-medium text-n-slate-12">
              <span :class="selected.icon" class="size-4" />
              {{ selected.label }}
            </h2>
            <p class="text-sm text-n-slate-11">{{ selected.help }}</p>
            <p class="mt-1 text-xs text-n-slate-10">
              Used by: {{ selected.usedBy }}
              <span class="mx-1">·</span>
              <template v-if="part.live">
                Last saved by {{ part.live.updated_by }}<template v-if="formatDate(part.live.updatedAt)">, {{ formatDate(part.live.updatedAt) }}</template>
              </template>
              <template v-else>Built-in text (never edited)</template>
            </p>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <Button
              :label="`History (${part.history.length})`"
              icon="i-lucide-history"
              variant="ghost"
              color="slate"
              size="sm"
              :disabled="!part.history.length"
              @click="showHistory = !showHistory"
            />
            <Button
              label="Reset to built-in"
              icon="i-lucide-rotate-ccw"
              variant="ghost"
              color="slate"
              size="sm"
              :disabled="isDefaultText"
              @click="resetToDefault"
            />
          </div>
        </div>

        <div v-if="showHistory" class="flex flex-col gap-1 p-2 border rounded-xl border-n-weak bg-n-alpha-1">
          <div
            v-for="version in part.history"
            :key="version.id"
            class="flex items-center gap-3 px-2 py-1.5 text-sm rounded-lg hover:bg-n-alpha-2"
          >
            <span class="text-xs shrink-0 text-n-slate-10 w-44">{{ formatDate(version.createdAt) }}</span>
            <span class="text-xs shrink-0 text-n-slate-11 w-28 truncate">{{ version.updated_by }}</span>
            <span dir="auto" class="flex-1 truncate text-n-slate-12">{{ version.prompt.slice(0, 140) }}</span>
            <Button label="Restore" size="xs" variant="faded" color="slate" @click="restore(version)" />
          </div>
        </div>

        <textarea
          v-model="draft"
          :aria-label="selected.label"
          dir="auto"
          spellcheck="false"
          class="flex-1 w-full p-4 text-base md:text-sm leading-relaxed border resize-none min-h-[420px] rounded-xl border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:border-n-brand"
        />

        <div class="flex flex-wrap items-center justify-between gap-3">
          <span class="text-xs text-n-slate-10">
            {{ draft.length.toLocaleString() }} characters
            <template v-if="isDirty(selectedKey)">
              <span class="mx-1">·</span><span class="text-n-amber-11">Unsaved changes</span>
            </template>
          </span>
          <div class="flex items-center gap-2">
            <Button
              label="Discard changes"
              variant="ghost"
              color="slate"
              size="sm"
              :disabled="!isDirty(selectedKey) || isSaving"
              @click="discard"
            />
            <Button
              label="Save"
              icon="i-lucide-check"
              size="sm"
              :is-loading="isSaving"
              :disabled="!isDirty(selectedKey) || isSaving"
              @click="save"
            />
          </div>
        </div>
      </section>

      <aside
        v-if="showTry"
        class="flex flex-col w-full min-h-[480px] border-t md:min-h-0 md:border-t-0 md:border-l md:w-[360px] md:max-w-[45%] shrink-0 border-n-weak bg-n-solid-1"
      >
        <div class="flex flex-col gap-2 p-3 border-b border-n-weak">
          <div class="flex items-center justify-between gap-2">
            <h2 class="text-sm font-medium text-n-slate-12">Try it</h2>
            <Button label="Clear" variant="ghost" color="slate" size="xs" :disabled="!tryMessages.length" @click="clearTry" />
          </div>
          <select
            :value="tryAgent"
            aria-label="Which AI to test"
            class="w-full px-2 py-1 text-sm border rounded-md border-n-weak bg-n-alpha-1 text-n-slate-12"
            @change="changeTryAgent($event.target.value)"
          >
            <option v-for="a in TRY_AGENTS" :key="a.key" :value="a.key">{{ a.label }} AI</option>
          </select>
          <p class="text-xs text-n-slate-10">
            Uses the current text<template v-if="unsavedCount"> including your {{ unsavedCount }} unsaved edit{{ unsavedCount > 1 ? 's' : '' }}</template>.
            Nothing is saved or sent to customers.
          </p>
        </div>
        <div class="flex flex-col flex-1 gap-2 p-3 overflow-y-auto">
          <p v-if="!tryMessages.length" class="text-xs text-center text-n-slate-10 mt-6">
            Write a message the way a customer would.
          </p>
          <div
            v-for="(m, i) in tryMessages"
            :key="i"
            class="flex flex-col gap-1"
            :class="m.role === 'user' ? 'items-end' : 'items-start'"
          >
            <div
              dir="auto"
              class="px-3 py-2 text-sm whitespace-pre-wrap rounded-xl max-w-[90%]"
              :class="
                m.role === 'user'
                  ? 'bg-n-brand text-white'
                  : m.role === 'error'
                    ? 'bg-n-ruby-3 text-n-ruby-11'
                    : 'bg-n-alpha-2 text-n-slate-12'
              "
            >{{ m.content }}</div>
            <span v-if="m.handoff" class="text-xs text-n-amber-11">
              🔔 Would alert the team: {{ m.handoffSummary }}
            </span>
          </div>
          <p v-if="trySending" class="text-xs text-n-slate-10">The AI is writing…</p>
        </div>
        <div class="flex items-end gap-2 p-3 border-t border-n-weak">
          <textarea
            v-model="tryInput"
            dir="auto"
            rows="2"
            aria-label="Test message"
            placeholder="مرحبا، بدي موقع لمطعمي"
            class="flex-1 px-2 py-1.5 text-base md:text-sm border rounded-lg resize-none border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-n-brand"
            @keydown.enter.exact.prevent="sendTry"
          />
          <Button label="Send" size="sm" :is-loading="trySending" :disabled="!tryInput.trim() || trySending" @click="sendTry" />
        </div>
      </aside>
    </div>
  </div>
</template>
